import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Heart, MessageCircle, Calendar, MapPin, Link as LinkIcon, Edit3, Trash2, X, Check, UserPlus, UserMinus, Mail, Ban, Flag, MoreVertical, FileText, Shield, AlertTriangle, Lock, Unlock } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDate, formatDistanceToNow } from '@/lib/utils'
import { sanitizeExternalUrl } from '@/lib/urlSanitizer'
import ConfirmModal from '@/components/ConfirmModal'
import { SkeletonCard, SkeletonText } from '@/components/Skeleton'

export default function Profile() {
  const { t } = useTranslation()
  const { username } = useParams()
  const { user, isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('posts')
  const [editingPost, setEditingPost] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [editTags, setEditTags] = useState('')
  const [showFollowers, setShowFollowers] = useState(false)
  const [showFollowing, setShowFollowing] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportReason, setReportReason] = useState('')
  const [showExpertModal, setShowExpertModal] = useState(false)
  const [expertType, setExpertType] = useState('psychologist')
  const [expertBio, setExpertBio] = useState('')
  const [confirmModal, setConfirmModal] = useState<{open: boolean; title: string; description?: string; onConfirm: () => void}>({ open: false, title: '', onConfirm: () => {} })

  const askConfirm = (title: string, description: string, onConfirm: () => void) => {
    setConfirmModal({ open: true, title, description, onConfirm })
  }

  const isMyProfile = user?.username === username
  const isModerator = user?.role === 'admin' || user?.role === 'moderator'

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', username],
    queryFn: () => api.get(`/users/search?username=${username}`).then(r => r.data?.[0]),
    enabled: !!username
  })

  const { data: posts, isLoading: postsLoading } = useQuery({
    queryKey: ['user-posts', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/posts`).then(r => r.data),
    enabled: !!profile?._id
  })

  const { data: isFollowing } = useQuery({
    queryKey: ['is-following', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/is-following`).then(r => r.data.following),
    enabled: !!profile?._id && isAuthenticated && !isMyProfile
  })

  const { data: followsMe } = useQuery({
    queryKey: ['follows-me', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/follows-me`).then(r => r.data.followsMe),
    enabled: !!profile?._id && isAuthenticated && !isMyProfile
  })

  const { data: isBlocked } = useQuery({
    queryKey: ['is-blocked', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/is-blocked`).then(r => r.data.blocked),
    enabled: !!profile?._id && isAuthenticated && !isMyProfile
  })

  const { data: followers } = useQuery({
    queryKey: ['followers', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/followers`).then(r => r.data),
    enabled: !!profile?._id && showFollowers
  })

  const { data: following } = useQuery({
    queryKey: ['following', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/following`).then(r => r.data),
    enabled: !!profile?._id && showFollowing
  })

  const { data: likedPosts, isLoading: likedPostsLoading } = useQuery({
    queryKey: ['user-liked', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/liked`).then(r => r.data),
    enabled: !!profile?._id && activeTab === 'liked' && isAuthenticated
  })

  const { data: savedPosts, isLoading: savedPostsLoading } = useQuery({
    queryKey: ['user-saved', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/saved`).then(r => r.data),
    enabled: !!profile?._id && activeTab === 'saved' && isAuthenticated
  })

  const { data: comments, isLoading: commentsLoading } = useQuery({
    queryKey: ['user-comments', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/comments`).then(r => r.data),
    enabled: !!profile?._id && activeTab === 'comments'
  })

  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ['user-events', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/events`).then(r => r.data),
    enabled: !!profile?._id && activeTab === 'events'
  })

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['user-stats', profile?._id],
    queryFn: () => api.get(`/users/${profile?._id}/stats`).then(r => r.data),
    enabled: !!profile?._id && activeTab === 'stats' && isMyProfile
  })

  const followMutation = useMutation({
    mutationFn: () => api.post(`/users/${profile?._id}/follow`),
    onSuccess: () => {
      queryClient.setQueryData(['profile', username], (old: any) => {
        if (!old) return old
        return { ...old, followersCount: (old.followersCount || 0) + 1 }
      })
      queryClient.invalidateQueries({ queryKey: ['is-following', profile?._id] })
      queryClient.invalidateQueries({ queryKey: ['followers', profile?._id] })
      toast({ title: t('profile.followed') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const unfollowMutation = useMutation({
    mutationFn: () => api.post(`/users/${profile?._id}/unfollow`),
    onSuccess: () => {
      queryClient.setQueryData(['profile', username], (old: any) => {
        if (!old) return old
        return { ...old, followersCount: Math.max(0, (old.followersCount || 0) - 1) }
      })
      queryClient.invalidateQueries({ queryKey: ['is-following', profile?._id] })
      queryClient.invalidateQueries({ queryKey: ['followers', profile?._id] })
      toast({ title: t('profile.unfollowed') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const blockMutation = useMutation({
    mutationFn: () => api.post(`/users/${profile?._id}/block`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['is-blocked', profile?._id] })
      toast({ title: t('profile.blocked') })
      setShowActions(false)
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const unblockMutation = useMutation({
    mutationFn: () => api.post(`/users/${profile?._id}/unblock`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['is-blocked', profile?._id] })
      toast({ title: t('profile.unblocked') })
      setShowActions(false)
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const reportMutation = useMutation({
    mutationFn: (reason: string) => api.post(`/users/${profile?._id}/report`, { reason }),
    onSuccess: () => {
      toast({ title: t('profile.reported') })
      setShowActions(false)
      setShowReportModal(false)
      setReportReason('')
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const deleteMutation = useMutation({
    mutationFn: (postId: string) => api.delete(`/posts/${postId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-posts'] })
      toast({ title: t('profile.postDeleted') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, message, tags }: { id: string; message: string; tags: string }) =>
      api.put(`/posts/${id}`, { message, tags }),
    onSuccess: () => {
      setEditingPost(null)
      queryClient.invalidateQueries({ queryKey: ['user-posts'] })
      toast({ title: t('profile.postUpdated') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const modPath = user?.role === 'admin' ? '/admin' : '/moderator'

  const warnMutation = useMutation({
    mutationFn: () => api.post(`${modPath}/users/${profile?._id}/warn`, { reason: t('profile.moderationReasons.violation') }),
    onSuccess: () => toast({ title: t('profile.warned') }),
    onError: (err: any) => toast({ title: err.response?.data?.error || err.response?.data?.message || t('common.error'), variant: 'destructive' })
  })

  const suspendMutation = useMutation({
    mutationFn: () => api.post(`${modPath}/users/${profile?._id}/suspend`, { duration: 7, reason: t('profile.moderationReasons.violation') }),
    onSuccess: () => toast({ title: t('profile.suspended') }),
    onError: (err: any) => toast({ title: err.response?.data?.error || err.response?.data?.message || t('common.error'), variant: 'destructive' })
  })

  const banMutation = useMutation({
    mutationFn: () => api.post(`${modPath}/users/${profile?._id}/ban`, { reason: t('profile.moderationReasons.seriousViolation') }),
    onSuccess: () => toast({ title: t('profile.banned') }),
    onError: (err: any) => toast({ title: err.response?.data?.error || err.response?.data?.message || t('common.error'), variant: 'destructive' })
  })

  const restrictPostsMutation = useMutation({
    mutationFn: () => api.post(`${modPath}/users/${profile?._id}/restrict-posts`),
    onSuccess: () => toast({ title: t('profile.restricted') }),
    onError: (err: any) => toast({ title: err.response?.data?.error || err.response?.data?.message || t('common.error'), variant: 'destructive' })
  })

  const unrestrictPostsMutation = useMutation({
    mutationFn: () => api.post(`${modPath}/users/${profile?._id}/unrestrict-posts`),
    onSuccess: () => toast({ title: t('profile.unrestricted') }),
    onError: (err: any) => toast({ title: err.response?.data?.error || err.response?.data?.message || t('common.error'), variant: 'destructive' })
  })

  const assignExpertMutation = useMutation({
    mutationFn: () => api.post(`/admin/users/${profile?._id}/assign-expert`, { expertType, expertBio }),
    onSuccess: () => {
      toast({ title: t('profile.expertAssigned') })
      setShowExpertModal(false)
      setExpertBio('')
      queryClient.invalidateQueries({ queryKey: ['profile', username] })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || err.response?.data?.message || t('common.error'), variant: 'destructive' })
  })

  const revokeExpertMutation = useMutation({
    mutationFn: () => api.post(`/admin/users/${profile?._id}/revoke-expert`, {}),
    onSuccess: () => {
      toast({ title: t('profile.expertRevoked') })
      queryClient.invalidateQueries({ queryKey: ['profile', username] })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || err.response?.data?.message || t('common.error'), variant: 'destructive' })
  })

  const handleEdit = (post: any) => {
    setEditingPost(post._id)
    setEditContent(post.message)
    setEditTags(post.tags?.join(', ') || '')
  }

  const handleSaveEdit = (postId: string) => {
    updateMutation.mutate({ id: postId, message: editContent, tags: editTags })
  }

  const handleDelete = (postId: string) => {
    askConfirm(t('profile.deletePost'), t('profile.deleteConfirm'), () => deleteMutation.mutate(postId))
  }

  // F1.12: Sanitize user-supplied website URL to prevent stored XSS via javascript: links
  const safeWebsite = profile?.website ? sanitizeExternalUrl(profile.website) : ''

  if (profileLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-secondary rounded-2xl" />
          <div className="flex items-end gap-4">
            <div className="w-24 h-24 rounded-full bg-secondary" />
            <div className="flex-1 space-y-2 pb-2">
              <div className="h-5 bg-secondary rounded w-1/3" />
              <div className="h-4 bg-secondary rounded w-1/4" />
            </div>
          </div>
          <SkeletonText count={2} className="pt-4" />
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center text-muted-foreground">
        {t('profile.notFound')}
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        description={confirmModal.description}
        variant="danger"
        onConfirm={() => { confirmModal.onConfirm(); setConfirmModal({ ...confirmModal, open: false }) }}
        onCancel={() => setConfirmModal({ ...confirmModal, open: false })}
      />
      <div className="relative">
        <div className="h-32 bg-gradient-to-r from-primary/30 to-primary/10" />
        <div className="px-4 pb-4">
          <div className="relative -mt-12 mb-4 flex items-end justify-between">
            <Avatar className="w-24 h-24 border-4 border-background">
              <AvatarImage src={profile.avatar} />
              <AvatarFallback>{profile.username[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            {!isMyProfile && isAuthenticated && (
              <div className="mb-2 flex items-center gap-2">
                <Link
                  to={`/messages/${profile._id}`}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-secondary hover:bg-secondary/80 transition-colors border border-border flex items-center gap-1"
                >
                  <Mail className="w-4 h-4" /> {t('profile.message')}
                </Link>
                <button
                  type="button"
                  aria-label={isFollowing ? t('profile.unfollow') : t('profile.follow')}
                  onClick={() => isFollowing ? unfollowMutation.mutate() : followMutation.mutate()}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isFollowing
                      ? 'bg-secondary text-foreground hover:bg-destructive/10 hover:text-destructive border border-border'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  }`}
                >
                  {isFollowing ? (
                    <span className="flex items-center gap-1"><UserMinus className="w-4 h-4" /> {t('profile.unfollow')}</span>
                  ) : (
                    <span className="flex items-center gap-1"><UserPlus className="w-4 h-4" /> {t('profile.follow')}</span>
                  )}
                </button>
                <div className="relative">
                  <button
                    type="button"
                    aria-label={t('profile.actions')}
                    onClick={() => setShowActions(!showActions)}
                    className="p-2 rounded-full text-muted-foreground hover:bg-secondary transition-colors"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  {showActions && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-xl shadow-lg z-20 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => isBlocked ? unblockMutation.mutate() : blockMutation.mutate()}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors"
                      >
                        <Ban className="w-4 h-4" />
                        {isBlocked ? t('profile.unblock') : t('profile.block')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowReportModal(true); setShowActions(false) }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors text-destructive"
                      >
                        <Flag className="w-4 h-4" />
                        {t('profile.report')}
                      </button>
                      {isModerator && (
                        <>
                          <div className="border-t border-border my-1" />
                          <button type="button" onClick={() => { setShowActions(false); askConfirm(t('profile.warn'), t('profile.confirmWarn'), () => warnMutation.mutate()); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors text-amber-500">
                            <AlertTriangle className="w-4 h-4" /> {t('profile.warn')}
                          </button>
                          <button type="button" onClick={() => { setShowActions(false); askConfirm(t('profile.suspend'), t('profile.confirmSuspend'), () => suspendMutation.mutate()); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors text-orange-500">
                            <Lock className="w-4 h-4" /> {t('profile.suspend')}
                          </button>
                          <button type="button" onClick={() => { setShowActions(false); askConfirm(t('profile.ban'), t('profile.confirmBan'), () => banMutation.mutate()); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors text-destructive">
                            <Ban className="w-4 h-4" /> {t('profile.ban')}
                          </button>
                          <button type="button" onClick={() => { restrictPostsMutation.mutate(); setShowActions(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors text-muted-foreground">
                            <Lock className="w-4 h-4" /> {t('profile.restrictPosts')}
                          </button>
                          <button type="button" onClick={() => { unrestrictPostsMutation.mutate(); setShowActions(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors text-muted-foreground">
                            <Unlock className="w-4 h-4" /> {t('profile.unrestrictPosts')}
                          </button>
                          {user?.role === 'admin' && (
                            <>
                              {profile?.expertType ? (
                                <button type="button" onClick={() => { setShowActions(false); askConfirm(t('profile.revokeExpert'), t('profile.confirmRevokeExpert'), () => revokeExpertMutation.mutate()); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors text-primary">
                                  <Shield className="w-4 h-4" /> {t('profile.revokeExpert')}
                                </button>
                              ) : (
                                <button type="button" onClick={() => { setShowExpertModal(true); setShowActions(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-secondary transition-colors text-primary">
                                  <Shield className="w-4 h-4" /> {t('profile.assignExpert')}
                                </button>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {isMyProfile && (
              <Link to="/settings" className="mb-2 px-4 py-2 rounded-full text-sm font-medium bg-secondary hover:bg-secondary/80 transition-colors border border-border">
                {t('profile.editProfile')}
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-xl font-bold">{profile.username}</h1>
            {profile.expertType && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/20 border border-primary/30 text-primary font-medium">
                {t('expert.types.' + profile.expertType)}
              </span>
            )}
            {profile.role === 'moderator' && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-500 font-medium">
                {t('admin.roles.moderator')}
              </span>
            )}
            {profile.role === 'admin' && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-500 font-medium">
                {t('admin.roles.admin')}
              </span>
            )}
            {followsMe && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary border border-border text-muted-foreground font-medium">
                {t('profile.followsYou')}
              </span>
            )}
          </div>
          <p className="text-muted-foreground text-sm">@{profile.username}</p>
          {profile.bio && <p className="mt-2 text-sm">{profile.bio}</p>}
          {profile.expertBio && (
            <div className="mt-2 p-3 rounded-lg bg-primary/5 border border-primary/10 text-sm">
              <p className="text-muted-foreground">{profile.expertBio}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
            {profile.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" /> {profile.location}
              </span>
            )}
            {profile.website && safeWebsite && (
              <a href={safeWebsite} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-primary">
                <LinkIcon className="w-4 h-4" /> {safeWebsite}
              </a>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" /> {formatDate(profile.createdAt)}
            </span>
          </div>

          <div className="flex gap-6 mt-4 py-3 border-y border-border">
            <div className="text-center">
              <p className="font-bold">{posts?.length || 0}</p>
              <p className="text-xs text-muted-foreground">{t('profile.posts')}</p>
            </div>
            <button type="button" aria-label={t('profile.followers')} onClick={() => { setShowFollowers(true); setShowFollowing(false); }} className="text-center hover:text-primary transition-colors">
              <p className="font-bold">{profile.followersCount || 0}</p>
              <p className="text-xs text-muted-foreground">{t('profile.followers')}</p>
            </button>
            <button type="button" aria-label={t('profile.following')} onClick={() => { setShowFollowing(true); setShowFollowers(false); }} className="text-center hover:text-primary transition-colors">
              <p className="font-bold">{profile.followingCount || 0}</p>
              <p className="text-xs text-muted-foreground">{t('profile.following')}</p>
            </button>
          </div>
        </div>
      </div>

      {/* Followers / Following Modal */}
      <AnimatePresence>
        {(showFollowers || showFollowing) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => { setShowFollowers(false); setShowFollowing(false); }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-sm max-h-[60vh] overflow-y-auto bg-card border border-border rounded-2xl p-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">{showFollowers ? t('profile.followers') : t('profile.following')}</h3>
                <button type="button" aria-label={t('common.close')} onClick={() => { setShowFollowers(false); setShowFollowing(false); }} className="p-1 rounded-lg hover:bg-secondary"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-2">
                {(showFollowers ? followers : following)?.map((u: any) => (
                  <Link key={u.userId} to={`/profile/${u.username}`} onClick={() => { setShowFollowers(false); setShowFollowing(false); }} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors">
                    <Avatar className="w-8 h-8"><AvatarImage src={u.avatar} /><AvatarFallback>{u.username[0]}</AvatarFallback></Avatar>
                    <span className="text-sm font-medium">{u.username}</span>
                  </Link>
                ))}
                {(!((showFollowers ? followers : following)?.length)) && (
                  <p className="text-center text-sm text-muted-foreground py-4">{t('common.empty')}</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex border-b border-border">
        {([
          'posts',
          ...(isAuthenticated ? ['liked', 'saved'] : []),
          'comments',
          'events',
          ...(isMyProfile ? ['stats'] : [])
        ] as string[]).map(tab => (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'posts' ? t('profile.posts')
              : tab === 'liked' ? t('profile.liked')
              : tab === 'saved' ? t('profile.saved')
              : tab === 'comments' ? t('profile.comments')
              : tab === 'events' ? t('profile.events')
              : t('profile.stats')}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-4">
        <AnimatePresence mode="wait">
          {(activeTab === 'posts' || activeTab === 'liked' || activeTab === 'saved') && (
            <motion.div
              key="posts-section"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {(() => {
                const displayPosts = activeTab === 'liked' ? likedPosts : activeTab === 'saved' ? savedPosts : posts
                const loading = activeTab === 'liked' ? likedPostsLoading : activeTab === 'saved' ? savedPostsLoading : postsLoading
                if (loading) {
                  return (
                    <div className="space-y-4">
                      <SkeletonCard />
                      <SkeletonCard />
                      <SkeletonCard />
                    </div>
                  )
                }
                if (!displayPosts || displayPosts.length === 0) {
                  return (
                    <div className="text-center py-12 text-muted-foreground">
                      {activeTab === 'liked' ? t('profile.noLiked') : activeTab === 'saved' ? t('profile.noSaved') : t('profile.noPosts')}
                    </div>
                  )
                }
                return (
                  <AnimatePresence>
                    {displayPosts.map((post: any) => (
                      <motion.div
                        key={post._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="p-4 rounded-xl bg-card border border-border group"
                      >
                        {editingPost === post._id && activeTab === 'posts' ? (
                          <div className="space-y-3">
                            <textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="w-full px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none min-h-[80px] text-sm"
                            />
                            <input
                              type="text"
                              value={editTags}
                              onChange={(e) => setEditTags(e.target.value)}
                              placeholder={t('profile.tagsPlaceholder')}
                              className="w-full px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleSaveEdit(post._id)}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90"
                              >
                                <Check className="w-3.5 h-3.5" /> {t('common.save')}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingPost(null)}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-secondary text-sm hover:bg-secondary/80"
                              >
                                <X className="w-3.5 h-3.5" /> {t('common.cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <Link to={`/post/${post._id}`}>
                              <p className="text-sm mb-2 hover:text-primary transition-colors">{post.message}</p>
                            </Link>
                            {post.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {post.tags.map((tag: string) => (
                                  <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">#{tag}</span>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {post.likes}</span>
                                <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {post.commentCount}</span>
                                <span>{formatDistanceToNow(post.createdAt)}</span>
                              </div>
                              {isMyProfile && activeTab === 'posts' && (
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    type="button"
                                    aria-label={t('common.edit')}
                                    onClick={() => handleEdit(post)}
                                    className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    aria-label={t('common.delete')}
                                    onClick={() => handleDelete(post._id)}
                                    className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )
              })()}
            </motion.div>
          )}

          {activeTab === 'comments' && (
            <motion.div
              key="comments-section"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {commentsLoading ? (
                <div className="space-y-4">
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </div>
              ) : (
                <>
                  <AnimatePresence>
                    {comments?.map((comment: any) => (
                      <motion.div
                        key={comment._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="p-4 rounded-xl bg-card border border-border"
                      >
                        <p className="font-bold text-sm">{comment.topicTitle}</p>
                        <p className="text-sm mt-1">{comment.content}</p>
                        <p className="text-xs text-muted-foreground mt-2">{formatDistanceToNow(comment.createdAt)}</p>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {(!comments || comments.length === 0) && (
                    <div className="text-center py-12 text-muted-foreground">{t('profile.noComments')}</div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {activeTab === 'events' && (
            <motion.div
              key="events-section"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {eventsLoading ? (
                <div className="space-y-4">
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </div>
              ) : (
                <>
                  <AnimatePresence>
                    {events?.map((event: any) => (
                      <motion.div
                        key={event._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="p-4 rounded-xl bg-card border border-border"
                      >
                        <p className="font-bold text-sm">{event.title}</p>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(event.date)}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.location}</span>
                           {event.isOnline && <span className="px-2 py-0.5 rounded-full bg-secondary">{t('common.online')}</span>}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {(!events || events.length === 0) && (
                    <div className="text-center py-12 text-muted-foreground">{t('profile.noEvents')}</div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {activeTab === 'stats' && (
            <motion.div
              key="stats-section"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              {statsLoading ? (
                <div className="grid grid-cols-2 gap-4">
                  <SkeletonCard className="h-32" />
                  <SkeletonCard className="h-32" />
                  <SkeletonCard className="h-32" />
                  <SkeletonCard className="h-32" />
                </div>
              ) : stats ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-card border border-border flex flex-col items-center gap-2">
                    <FileText className="w-6 h-6 text-primary" />
                    <p className="text-2xl font-bold">{stats.postCount || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('profile.posts')}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-card border border-border flex flex-col items-center gap-2">
                    <MessageCircle className="w-6 h-6 text-primary" />
                    <p className="text-2xl font-bold">{stats.commentCount || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('profile.comments')}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-card border border-border flex flex-col items-center gap-2">
                    <Heart className="w-6 h-6 text-primary" />
                    <p className="text-2xl font-bold">{stats.likeCount || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('profile.likesReceived')}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-card border border-border flex flex-col items-center gap-2">
                    <Calendar className="w-6 h-6 text-primary" />
                    <p className="text-2xl font-bold">{stats.eventCount || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('profile.eventsJoined')}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">{t('common.empty')}</div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Report Modal */}
      <AnimatePresence>
        {showReportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowReportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-sm bg-card border border-border rounded-2xl p-5"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-semibold mb-3">{t('profile.report')}</h3>
              <p className="text-sm text-muted-foreground mb-3">{profile?.username}</p>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm mb-4"
              >
                <option value="">{t('profile.reportReasonPlaceholder')}</option>
                <option value="inappropriate">{t('profile.reportReason.inappropriate')}</option>
                <option value="spam">{t('profile.reportReason.spam')}</option>
                <option value="hate">{t('profile.reportReason.hate')}</option>
                <option value="fraud">{t('profile.reportReason.fraud')}</option>
                <option value="other">{t('profile.reportReason.other')}</option>
              </select>
              <div className="flex gap-2">
                <button
                  onClick={() => reportMutation.mutate(reportReason)}
                  disabled={!reportReason || reportMutation.isPending}
                  className="flex-1 py-2 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 disabled:opacity-50 transition-colors"
                >
                  {t('profile.report')}
                </button>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 py-2 rounded-lg bg-secondary text-sm hover:bg-secondary/80 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expert Assignment Modal */}
      <AnimatePresence>
        {showExpertModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowExpertModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-sm bg-card border border-border rounded-2xl p-5"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-semibold mb-3">{t('profile.assignExpert')}</h3>
              <p className="text-sm text-muted-foreground mb-3">{profile?.username}</p>
              <label className="block text-sm font-medium mb-2">{t('profile.expertTypeLabel')}</label>
              <select
                value={expertType}
                onChange={(e) => setExpertType(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm mb-3"
              >
                <option value="psychologist">{t('expert.types.psychologist')}</option>
                <option value="psychiatrist">{t('expert.types.psychiatrist')}</option>
                <option value="counselor">{t('expert.types.counselor')}</option>
                <option value="life_coach">{t('expert.types.life_coach')}</option>
                <option value="social_worker">{t('expert.types.social_worker')}</option>
                <option value="other">{t('expert.types.other')}</option>
              </select>
              <label className="block text-sm font-medium mb-2">{t('profile.expertBioLabel')}</label>
              <textarea
                value={expertBio}
                onChange={(e) => setExpertBio(e.target.value)}
                placeholder={t('profile.expertBioPlaceholder')}
                className="w-full px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none min-h-[60px] text-sm mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => assignExpertMutation.mutate()}
                  disabled={assignExpertMutation.isPending}
                  className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {t('profile.assignExpert')}
                </button>
                <button
                  onClick={() => setShowExpertModal(false)}
                  className="flex-1 py-2 rounded-lg bg-secondary text-sm hover:bg-secondary/80 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
